import { useEffect, useRef, useState } from "react";

export interface LimitedTextDraft {
  /** What the control renders. The DRAFT, which may exceed the limit. */
  readonly value: string;
  readonly length: number;
  /** How far past the limit the draft is; `0` while it is within. */
  readonly overBy: number;
  /** Present exactly when the draft is not committable. */
  readonly message: string | null;
  readonly onChange: (next: string) => void;
  readonly onFocus: () => void;
  readonly onBlur: () => void;
}

/**
 * Hold a free-text draft that may exceed what the widget's schema will store,
 * and say so, instead of dropping it (PB-1575).
 *
 * The defect this replaces had three parts, and the middle one is the reason a
 * plain `maxLength` is not the fix:
 *
 *  1. The card's textarea carried no limit, no counter and no `aria-invalid`,
 *     so nothing warned anyone they were approaching one.
 *  2. An over-limit value was DROPPED SILENTLY — `SqlWidgetInner.handleChange`
 *     `safeParse`s against the kind's schema and `return`s on failure with a
 *     `console.warn`. The user's text vanished with no on-screen trace.
 *  3. The expanded editor had `maxLength={2000}`, which silently TRUNCATES a
 *     paste at the cap. Quieter than (2), and worse: the text looks saved.
 *
 * So this deliberately allows the over-limit value to exist in the control,
 * refuses to commit it, and names the reason. Four properties are load-bearing:
 *
 *  - **The message is DERIVED from the draft, never stored.** It has to clear
 *    on the keystroke that fixes the value, and a stored flag lags by one.
 *  - **A valid draft commits on every keystroke**, exactly as before. Nothing
 *    about the normal editing path changes — the widget resolver still
 *    debounces the write.
 *  - **Blur re-seeds from the committed value ONLY when the draft is valid.**
 *    That keeps the `DraftField` behaviour of picking up remote edits, while
 *    an over-limit draft SURVIVES blur so it can be corrected rather than
 *    thrown away — which is the whole complaint.
 *  - **An external change is adopted only while unfocused**, so a peer's edit
 *    never overwrites text mid-sentence.
 */
export function useLimitedTextDraft(
  committed: string,
  maxLength: number,
  commit: (next: string) => void,
): LimitedTextDraft {
  const [draft, setDraft] = useState(committed);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(committed);
  }, [committed]);

  const overBy = Math.max(0, draft.length - maxLength);

  return {
    value: draft,
    length: draft.length,
    overBy,
    message:
      overBy > 0
        ? `${overBy.toLocaleString()} character${overBy === 1 ? "" : "s"} over the ${maxLength.toLocaleString()} limit. Shorten it to save.`
        : null,
    onChange: (next: string): void => {
      setDraft(next);
      if (next.length <= maxLength) commit(next);
    },
    onFocus: (): void => {
      focusedRef.current = true;
    },
    onBlur: (): void => {
      focusedRef.current = false;
      // Over-limit text is kept on purpose — see the docblock.
      if (draft.length <= maxLength) setDraft(committed);
    },
  };
}
